import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { accessTokenStore } from './accessTokenStore';
import { ensureAccessToken, runRefresh } from './httpClient';
import { logoutSession } from './authApi';
import { clearRememberMeCookie, isRememberMeEnabled } from './cookies';
import { shouldPersistAccessToken } from './tokenPersist';
import { logout as logoutAction } from '../store/slices/authSlice';

const AuthTokenContext = createContext(null);

export function AuthTokenProvider({ children }) {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const rememberMeFlag = useSelector((state) => state.auth.rememberMe);
  const userId = useSelector((state) => state.auth.user?.userId ?? null);
  const [accessToken, setAccessTokenState] = useState(() => {
    const existing = accessTokenStore.get();
    if (existing) return existing;
    if (shouldPersistAccessToken(rememberMeFlag)) {
      return accessTokenStore.restoreFromSession();
    }
    return null;
  });
  const [bootstrapped, setBootstrapped] = useState(() => {
    if (!isAuthenticated) return true;
    return Boolean(accessTokenStore.get());
  });
  const bootstrappedRef = useRef(bootstrapped);

  useEffect(() => {
    return accessTokenStore.subscribe((token) => {
      setAccessTokenState(token);
    });
  }, []);

  // Reload: memory token gone.
  // rememberMe=false → restore JWT from sessionStorage (tab memory).
  // rememberMe=true → silent refresh (HttpOnly cookie).
  useEffect(() => {
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;

    if (!isAuthenticated) {
      setBootstrapped(true);
      return;
    }

    if (shouldPersistAccessToken(rememberMeFlag) && !accessTokenStore.get()) {
      accessTokenStore.restoreFromSession();
    }

    if (accessTokenStore.get()) {
      setBootstrapped(true);
      return;
    }

    if (!isRememberMeEnabled(rememberMeFlag)) {
      dispatch(logoutAction());
      setBootstrapped(true);
      return;
    }

    void (async () => {
      try {
        await runRefresh();
      } catch {
        accessTokenStore.clear();
        clearRememberMeCookie();
        dispatch(logoutAction());
      } finally {
        setBootstrapped(true);
      }
    })();
  }, [dispatch, isAuthenticated, rememberMeFlag]);

  const setAccessToken = useCallback((token, options) => {
    accessTokenStore.set(token, options);
  }, []);

  const clearAccessToken = useCallback(() => {
    accessTokenStore.clear();
  }, []);

  const logout = useCallback(
    async ({ skipApi = false } = {}) => {
      const uid = userId;
      accessTokenStore.clear();
      clearRememberMeCookie();
      dispatch(logoutAction());

      if (!skipApi) {
        try {
          await logoutSession({ uid });
        } catch {
          // Client state already cleared
        }
      }
    },
    [dispatch, userId]
  );

  const value = useMemo(
    () => ({
      accessToken,
      setAccessToken,
      clearAccessToken,
      ensureAccessToken,
      logout,
    }),
    [accessToken, setAccessToken, clearAccessToken, logout]
  );

  if (!bootstrapped) return null;

  return <AuthTokenContext.Provider value={value}>{children}</AuthTokenContext.Provider>;
}

export function useAuthToken() {
  const ctx = useContext(AuthTokenContext);
  if (!ctx) {
    throw new Error('useAuthToken must be used within AuthTokenProvider');
  }
  return ctx;
}
